import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import ModuleAccordion from "./course/ModuleAccordion";
import CourseHeader, { BackToCourses } from "./course/CourseHeader";

export default function CourseDetail() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [openModule, setOpenModule] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/courses/${courseId}`);
      setData(res.data);
      if (res.data?.modules?.length) {
        setOpenModule((prev) => prev ?? res.data.modules[0].id);
      }
    } catch (e) {
      if (e?.response?.status === 404) nav("/courses");
    }
  }, [courseId, nav]);

  useEffect(() => { load(); }, [load]);

  const toggleLesson = useCallback(async (moduleId, lesson) => {
    if (busyId) return;
    const done = data.completed_lesson_ids.includes(lesson.id);
    setBusyId(lesson.id);
    try {
      await api.post("/me/lesson", {
        course_id: courseId, module_id: moduleId, lesson_id: lesson.id, completed: !done,
      });
      await load();
    } finally {
      setBusyId(null);
    }
  }, [busyId, data, courseId, load]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[var(--bg)]">
        <TopNav />
        <div className="max-w-7xl mx-auto px-6 py-10 text-[var(--text-2)]">Loading…</div>
      </div>
    );
  }

  const { course, modules, completed_lesson_ids: doneIds, stats, enrollment } = data;
  const certified = stats.progress >= 100;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <BackToCourses />
        <CourseHeader course={course} stats={stats} certified={certified} />

        <div className="mt-8 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">// SYLLABUS</div>
              <h2 className="font-display font-bold text-2xl uppercase mt-1">Modules &amp; Lessons</h2>
            </div>
            <div className="font-mono text-xs text-[var(--text-2)] uppercase tracking-widest">
              {modules.length} modules
            </div>
          </div>

          {modules.map((m, idx) => (
            <ModuleAccordion
              key={m.id}
              module={m}
              idx={idx}
              isOpen={openModule === m.id}
              onToggleOpen={(id) => setOpenModule(openModule === id ? null : id)}
              doneIds={doneIds}
              busyId={busyId}
              onToggleLesson={toggleLesson}
            />
          ))}
        </div>

        {enrollment && (
          <div className="mt-8 font-mono text-xs text-[var(--text-2)] uppercase tracking-widest">
            ENROLLED // {new Date(enrollment.enrolled_at).toLocaleDateString()} &middot; STATUS:{" "}
            <span className={certified ? "text-[var(--green)]" : "text-[var(--cyan)]"}>{enrollment.status}</span>
          </div>
        )}
      </main>
    </div>
  );
}
